// AI Translate client — the front-end half of the Mistral practice loop.
//
// The browser never sees the Mistral key. These functions call two custom
// PocketBase routes (pb_hooks/ai.pb.js) via pb.send(), which attaches the
// signed-in user's auth token automatically. The hook holds the key, talks to
// Mistral, persists the attempt, and rolls the user's level estimate.
import { pb } from "./pocketbase.js";
import { isAuthed, currentUser } from "./auth.js";

// Ask the backend to generate one English sentence for the chosen area.
// area = { tier, theme, tense } — theme/tense are human-readable strings (or "").
export async function generateSentence(area = {}) {
  if (!isAuthed()) throw new Error("Sign in to use AI practice.");
  return pb.send("/api/ai/generate", {
    method: "POST",
    body: {
      tier: area.tier || "A2",
      theme: area.theme || "",
      tense: area.tense || "",
      focus: area.focusDomain || "",
    },
  });
}

// Send the learner's translation for grading. Resolves to
// { verdict, corrected, alternatives, explanation, score, level }.
export async function evaluateTranslation({ english, answer, tier, theme, tense } = {}) {
  if (!isAuthed()) throw new Error("Sign in to use AI practice.");
  return pb.send("/api/ai/evaluate", {
    method: "POST",
    body: {
      english: english || "",
      answer: answer || "",
      tier: tier || "A2",
      theme: theme || "",
      tense: tense || "",
    },
  });
}

// Look up a word the dictionary doesn't have yet. The backend checks the
// dictionary first (no quota) and only calls Mistral on a true miss, caching the
// result. Resolves to { entry, cached }.
export async function defineWord(word) {
  if (!isAuthed()) throw new Error("Sign in to look up new words.");
  return pb.send("/api/ai/define", { method: "POST", body: { word: word || "" } });
}

// Generate a themed vocabulary set (session-only flashcards). Resolves to
// { theme, tier, cards:[{es,en,pos}] }. Counts against the daily AI limit.
export async function generateVocab({ theme, tier } = {}) {
  if (!isAuthed()) throw new Error("Sign in to generate vocabulary.");
  return pb.send("/api/ai/vocab", { method: "POST", body: { theme: theme || "", tier: tier || "A1" } });
}

// The user's learning profile row (carries ai_level, ai_score_ema, counts).
export async function fetchAiProfile() {
  if (!isAuthed()) return null;
  const uid = currentUser()?.id;
  if (!uid) return null;
  try {
    return await pb.collection("user_profiles").getFirstListItem(`user_id = "${uid}"`);
  } catch {
    return null; // no profile yet (created on first graded attempt)
  }
}

// Recent attempts, newest first — for an optional history panel.
export async function fetchAiAttempts(limit = 20) {
  if (!isAuthed()) return [];
  const uid = currentUser()?.id;
  if (!uid) return [];
  try {
    const res = await pb.collection("ai_attempts").getList(1, limit, {
      filter: `user_id = "${uid}"`,
      sort: "-created",
    });
    return res.items || [];
  } catch {
    return [];
  }
}

// Aggregate the learner's classified errors over a recent window, optionally
// scoped to one level (tier). Returns per-domain and per-type counts plus the
// number of attempts in the same window (the denominator for context). Counting
// is done client-side — fine for a personal app's volume, and keeps the query
// to two simple list calls.
export async function fetchErrorStats({ days = 30, tier = "" } = {}) {
  const empty = { attempts: 0, totalErrors: 0, byDomain: {}, byType: {}, windowDays: days, tier };
  if (!isAuthed()) return empty;
  const uid = currentUser()?.id;
  if (!uid) return empty;
  const since = new Date(Date.now() - days * 86400000).toISOString().slice(0, 10) + " 00:00:00.000Z";
  let errFilter = `user_id = "${uid}" && created >= "${since}"`;
  let attFilter = `user_id = "${uid}" && created >= "${since}"`;
  if (tier) {
    errFilter += ` && tier = "${tier}"`;
    attFilter += ` && tier = "${tier}"`;
  }
  try {
    const rows = await pb.collection("attempt_errors").getFullList({ filter: errFilter, sort: "-created" });
    const byDomain = {};
    const byType = {};
    for (const r of rows) {
      const d = r.domain || "other";
      const t = r.type || "other";
      byDomain[d] = (byDomain[d] || 0) + 1;
      byType[t] = (byType[t] || 0) + 1;
    }
    // Verdict counts give both the volume (attempts = their sum) and the
    // accuracy overlay. Three tiny count queries — cheaper than pulling rows.
    const vcount = async (verdict) => {
      try {
        const r = await pb.collection("ai_attempts").getList(1, 1, { filter: `${attFilter} && verdict = "${verdict}"` });
        return r.totalItems || 0;
      } catch {
        return 0;
      }
    };
    const [correct, partial, incorrect] = await Promise.all([vcount("correct"), vcount("partial"), vcount("incorrect")]);
    const attempts = correct + partial + incorrect;
    const accuracy = attempts ? Math.round((correct / attempts) * 100) : 0;
    return { attempts, correct, partial, incorrect, accuracy, totalErrors: rows.length, byDomain, byType, windowDays: days, tier };
  } catch {
    return empty;
  }
}

// Pull the friendly server message out of a PocketBase request failure. Our
// hook always answers errors as { message }, surfaced on the thrown error's
// response/data depending on SDK version.
export function aiErrorMessage(err) {
  if (!err) return "Something went wrong. Please try again.";
  const r = err.response || err.data;
  if (r && typeof r.message === "string" && r.message) return r.message;
  if (typeof err.message === "string" && err.message) return err.message;
  return "Something went wrong. Please try again.";
}
